/**
 * As poses do battler lateral, na tabela do RPG Maker MZ.
 *
 * Uma folha de `sv_actors` tem 9 colunas por 6 linhas — 54 quadros — e não é
 * uma tira contínua: são 18 poses de 3 quadros cada, dispostas em três blocos
 * de coluna. Sem essa tabela o índice do quadro vira chute, e chutar erra de um
 * jeito que passa despercebido: o quadro 28 é uma pose plausível de personagem
 * com a arma erguida, só que é `guard` — defender —, e era ela que o jogo vinha
 * mostrando toda vez que alguém atacava.
 *
 * A aritmética é a de `Sprite_Actor.prototype.updateFrame` do acervo:
 *
 *     cx = floor(motion / 6) * 3 + pattern
 *     cy = motion % 6
 *
 * Daí o índice global na folha é `cy * 9 + cx`.
 */

/** As dezoito poses, na ordem em que o acervo as numera. */
export const MOTIONS = {
  walk: 0,
  wait: 1,
  chant: 2,
  guard: 3,
  damage: 4,
  evade: 5,
  thrust: 6,
  swing: 7,
  missile: 8,
  skill: 9,
  spell: 10,
  item: 11,
  escape: 12,
  victory: 13,
  dying: 14,
  abnormal: 15,
  sleep: 16,
  dead: 17,
} as const;

export type MotionName = keyof typeof MOTIONS;

/**
 * Poses que repetem enquanto durarem; as demais tocam uma vez e param no fim.
 *
 * A distinção é a do acervo e importa na tela: `dead` em laço mantém o corpo
 * caído no chão, enquanto `swing` precisa parar no último quadro — se voltasse
 * ao começo, o golpe pareceria um tique nervoso.
 */
const LOOPING = new Set<MotionName>([
  'walk', 'wait', 'chant', 'guard', 'escape', 'victory', 'dying', 'abnormal', 'sleep', 'dead',
]);

export function isLooping(motion: MotionName): boolean {
  return LOOPING.has(motion);
}

/** Quantos quadros o acervo dedica a cada pose. */
export const PATTERNS_PER_MOTION = 3;

/** Colunas e linhas de uma folha de `sv_actors`. */
export const SHEET_COLUMNS = 9;
export const SHEET_ROWS = 6;

/**
 * O quadro da pose, no índice global que `frameRect` entende.
 *
 * `pattern` percorre 0,1,2 e depois volta a 1 — o vaivém do acervo, que é o que
 * dá a uma pose parada a respiração de quem está vivo.
 */
export function motionFrame(motion: MotionName, pattern: number): number {
  const index = MOTIONS[motion];
  const step = pattern < PATTERNS_PER_MOTION ? pattern : 1;
  const column = Math.floor(index / SHEET_ROWS) * PATTERNS_PER_MOTION + step;
  const row = index % SHEET_ROWS;
  return row * SHEET_COLUMNS + column;
}

/**
 * O ciclo de quadros de uma pose, na ordem em que devem passar na tela.
 *
 * Pose em laço vai e volta (0,1,2,1); pose de uma vez só avança e fica parada
 * no último quadro, que é onde o golpe "termina".
 */
export function motionCycle(motion: MotionName): number[] {
  const steps = isLooping(motion) ? [0, 1, 2, 1] : [0, 1, 2];
  return steps.map((p) => motionFrame(motion, p));
}

/**
 * Com que pose esta arma ataca.
 *
 * O acervo declara isso em `System.attackMotions`, indexado pelo tipo de arma:
 * lâminas e cajados giram (`swing`), lanças e garras estocam (`thrust`), arcos
 * e armas de fogo disparam (`missile`). Magia e cura fogem da tabela de armas
 * do acervo — no RPG Maker elas são habilidades, e habilidade lança `spell`.
 */
export function attackMotion(
  weapon: { readonly type?: string; readonly magic?: boolean; readonly heals?: boolean } | null,
): MotionName {
  if (!weapon) return 'thrust'; // desarmado: soco, como o acervo faz
  if (weapon.heals) return 'skill';
  if (weapon.magic) return 'spell';

  switch (weapon.type) {
    case 'lanca':
    case 'garra':
    case 'luva':
      return 'thrust';
    case 'arco':
    case 'besta':
    case 'arma_de_fogo':
      return 'missile';
    case 'cajado':
      return 'spell';
    case 'espada':
    case 'adaga':
    case 'machado':
    case 'malho':
    case 'chicote':
      return 'swing';
    default:
      return 'swing';
  }
}
